import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AzureStorageMutatedEvent } from '../../../types/azureStorage';
import { storageNodeId } from '../../explorer/models/azureStorageNodeFactories';
import type { StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { findNodeById } from '../../explorer/services/treeService';
import { useDatabaseStore } from '../store/databaseStore';
import { loadStorageServiceFolder } from '../services/azureStorageListing';
import { invalidateStoragePreviewContent } from '../../workspace/storage-explorer/preview/useStoragePreviewContent';

async function syncMutation(event: AzureStorageMutatedEvent, queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ['azure-storage', event.connectionId] });
  // The file preview re-reads too: an object without an ETag (an NFS share) keeps its cache key across a change.
  invalidateStoragePreviewContent(queryClient, event.connectionId);
  const store = useDatabaseStore.getState();
  const folderId = storageNodeId(event.connectionId, { kind: 'service', service: event.service });
  const node = findNodeById(folderId, store.treeData);
  if (!node || node.type !== 'storage-service-folder') return;
  await loadStorageServiceFolder({
    node: node as StorageServiceFolderNode,
    queryClient,
    updateNode: store.updateNode,
    forceReload: true,
    keepRows: true,
  });
}

/** Keeps TanStack listings and the global Object Explorer tree in sync with UI and MCP mutations. */
export function useStorageMutationSync(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const subscribe = window.electronAPI?.azureStorage?.onMutated;
    if (!subscribe) return;
    return subscribe((event) => {
      void syncMutation(event, queryClient);
    });
  }, [queryClient]);
}
